import './Loader.css'

export type LoaderVariant = 'brand' | 'pcb' | 'bus' | 'ring'

export interface LoaderProps {
  /** Тип лоадера. По умолчанию 'brand' для full-screen, 'bus' для inline. */
  variant?: LoaderVariant
  /** Размер в px. По умолчанию зависит от variant. */
  size?: number
  /** Прогресс 0–100 (только для variant='ring'). Без него — indeterminate. */
  progress?: number
  /** Сообщение под лоадером. */
  label?: string
  /** Занять весь viewport с backdrop. */
  fullscreen?: boolean
  className?: string
}

export function Loader({
  variant = 'brand',
  size,
  progress,
  label,
  fullscreen = false,
  className = '',
}: LoaderProps) {
  const defaultSize = { brand: 72, pcb: 72, bus: 120, ring: 60 }[variant]
  const s = size ?? defaultSize

  const content = (
    <div className={`mh-loader mh-loader--${variant} ${className}`}>
      {variant === 'brand' && <BrandMark size={s} />}
      {variant === 'pcb' && <PcbMark size={s} />}
      {variant === 'bus' && <BusMark size={s} />}
      {variant === 'ring' && <RingMark size={s} progress={progress} />}
      {label && <div className="mh-loader__label">{label}</div>}
    </div>
  )

  if (fullscreen) {
    return (
      <div className="mh-loader__fullscreen" role="status" aria-live="polite">
        {content}
      </div>
    )
  }
  return content
}

/* ────────────── Variants ────────────── */

function BrandMark({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className="mh-l1">
      <g className="mh-l1__sat">
        <line
          x1="50"
          y1="50"
          x2="22"
          y2="22"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
        <circle cx="22" cy="22" r="7" fill="currentColor" />
      </g>
      <g className="mh-l1__sat mh-l1__sat--b">
        <line
          x1="50"
          y1="50"
          x2="22"
          y2="22"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
        <circle cx="22" cy="22" r="7" fill="currentColor" />
      </g>
      <g className="mh-l1__sat mh-l1__sat--c">
        <line
          x1="50"
          y1="50"
          x2="22"
          y2="22"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
        <circle cx="22" cy="22" r="7" fill="currentColor" />
      </g>
      <circle className="mh-l1__core" cx="50" cy="50" r="13" fill="currentColor" />
      <circle cx="50" cy="50" r="6" fill="#fff" />
    </svg>
  )
}

function PcbMark({ size }: { size: number }) {
  const traces = [
    'M10 30 L40 30 L50 40 L50 55',
    'M110 30 L80 30 L70 40 L70 55',
    'M10 90 L40 90 L50 80 L50 65',
    'M110 90 L80 90 L70 80 L70 65',
  ]
  return (
    <svg viewBox="0 0 120 120" width={size} height={size} className="mh-l2">
      {traces.map((d, i) => (
        <path key={`t${i}`} className="mh-l2__trace" d={d} />
      ))}
      <circle className="mh-l2__pad" cx="10" cy="30" r="3.5" />
      <circle className="mh-l2__pad" cx="110" cy="30" r="3.5" />
      <circle className="mh-l2__pad" cx="10" cy="90" r="3.5" />
      <circle className="mh-l2__pad" cx="110" cy="90" r="3.5" />
      <rect className="mh-l2__chip" x="45" y="55" width="30" height="30" rx="3" />
      <rect className="mh-l2__chipFrame" x="45" y="55" width="30" height="30" rx="3" />
      <circle cx="50" cy="60" r="1.8" fill="#fff" />
      {traces.map((d, i) => (
        <path key={`e${i}`} className={`mh-l2__electron mh-l2__electron--${i}`} d={d} />
      ))}
    </svg>
  )
}

function BusMark({ size }: { size: number }) {
  return (
    <div className="mh-l5" style={{ width: size }}>
      <span className="mh-l5__cap mh-l5__cap--l">
        <span className="mh-l5__capInner" />
      </span>
      <span className="mh-l5__rail" />
      <span className="mh-l5__dot" />
      <span className="mh-l5__dot mh-l5__dot--b" />
      <span className="mh-l5__dot mh-l5__dot--c" />
      <span className="mh-l5__cap mh-l5__cap--r">
        <span className="mh-l5__capInner" />
      </span>
    </div>
  )
}

function RingMark({ size, progress }: { size: number; progress?: number }) {
  const r = 42
  const circ = 2 * Math.PI * r
  const determinate = typeof progress === 'number'
  const offset = determinate ? circ * (1 - Math.max(0, Math.min(100, progress!)) / 100) : undefined

  return (
    <div
      className={`mh-l6 ${determinate ? 'mh-l6--det' : 'mh-l6--indet'}`}
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 100 100">
        <circle className="mh-l6__track" cx="50" cy="50" r={r} />
        <circle
          className="mh-l6__prog"
          cx="50"
          cy="50"
          r={r}
          strokeDasharray={circ}
          strokeDashoffset={offset}
        />
      </svg>
      {determinate && (
        <div className="mh-l6__pct">
          {Math.round(progress!)}
          <sup>%</sup>
        </div>
      )}
    </div>
  )
}
